/**
 * Confirmation contract for the dedicated "Delete account" page.
 *
 * To proceed with the (irreversible-after-grace-period) deletion the user must
 * re-type the EXACT email of the account being deleted into a confirmation box.
 * This is a deliberate friction step against ACCIDENTAL deletion and against
 * confusing "Delete account" with "Sign out". It is NOT a security boundary —
 * the real authorization is the session cookie the backend checks on
 * `requestAccountDeletion`; this only gates the destructive button in the UI.
 *
 * Matching is normalized with trim + lowercase (case-insensitive):
 *   - Email domains are case-insensitive by spec, and every mainstream provider
 *     also treats the local-part case-insensitively, so requiring exact case
 *     would only produce false negatives that frustrate a legitimate user.
 *   - A leading/trailing space from autofill or copy-paste should not block a
 *     correct confirmation.
 * The trade-off (someone could type a different-cased variant) is irrelevant:
 * this is an anti-mistake gate, not authentication.
 */
export function normalizeEmailForConfirmation(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Returns `true` only when `input` matches the account email under the
 * normalization rules above. An empty/absent account email never matches, so a
 * missing session can never accidentally enable the destructive button.
 */
export function emailMatchesAccount(
  input: string,
  accountEmail: string | null | undefined,
): boolean {
  if (!accountEmail) {
    return false;
  }
  const normalizedAccount = normalizeEmailForConfirmation(accountEmail);
  if (normalizedAccount === "") {
    return false;
  }
  return normalizeEmailForConfirmation(input) === normalizedAccount;
}
